import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, Form, Input, Button,
  Typography, Space, Divider, Alert, Spin, Result, Steps, Tag, Avatar,
  Row, Col, Badge
} from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined,
  CalendarOutlined, CheckCircleOutlined, InfoCircleOutlined,
  TeamOutlined, ClockCircleOutlined, LeftOutlined, RightOutlined
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import API_URL from '../../config/api';

dayjs.extend(isoWeek);

const { Title, Text, Paragraph } = Typography;

interface Business {
  id: number;
  name: string;
  logo?: string;
  address?: string;
  phone?: string;
}

interface Service {
  id: number;
  name: string;
  price: number;
  description: string;
  duration?: number;
}

interface Staff {
  id: string;
  name: string;
  role?: string;
  photo_url?: string;
}

const ANYONE_ID = '__anyone__';

export default function PublicBooking({ successRedirect = false }: { successRedirect?: boolean }) {
  const { businessId } = useParams<{ businessId: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [business, setBusiness] = useState<Business | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(successRedirect);

  const [form] = Form.useForm();
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [anyoneAvailable, setAnyoneAvailable] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Calendar week state — week starting Monday
  const [weekStart, setWeekStart] = useState<Dayjs>(() => dayjs().startOf('isoWeek'));
  // weekSlots: { [dateStr]: { time: string; available: boolean }[] }
  const [weekSlots, setWeekSlots] = useState<Record<string, { time: string; available: boolean }[]>>({});
  const [loadingWeek, setLoadingWeek] = useState(false);

  // Generate the 7 days of the current displayed week
  const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

  // All 30-min time-slot labels (08:00–19:00) — just for row headers
  const TIME_ROWS: string[] = [];
  for (let h = 8; h < 19; h++) {
    TIME_ROWS.push(`${String(h).padStart(2, '0')}:00`);
    TIME_ROWS.push(`${String(h).padStart(2, '0')}:30`);
  }
  TIME_ROWS.push('19:00');

  useEffect(() => {
    if (successRedirect) setSuccess(true);
    if (businessId) loadData();
  }, [businessId, successRedirect]);

  // Fetch availability for every day in the displayed week
  const fetchWeek = useCallback(async () => {
    if (!businessId) return;
    setLoadingWeek(true);
    const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));
    const results: Record<string, { time: string; available: boolean }[]> = {};
    await Promise.all(
      days.map(async (day) => {
        const dateStr = day.format('YYYY-MM-DD');
        try {
          const params = new URLSearchParams({ businessId: businessId!, date: dateStr });
          if (anyoneAvailable) params.set('anyoneAvailable', 'true');
          else if (selectedStaff) params.set('staffId', selectedStaff.id);
          const res = await fetch(`${API_URL}/bookings/public/availability?${params}`);
          if (res.ok) {
            const data = await res.json();
            results[dateStr] = data.slots ?? [];
          } else {
            results[dateStr] = [];
          }
        } catch {
          results[dateStr] = [];
        }
      })
    );
    setWeekSlots(results);
    setLoadingWeek(false);
  }, [businessId, weekStart, selectedStaff, anyoneAvailable]);

  useEffect(() => {
    setSelectedSlot(null);
    setSelectedDate(null);
    fetchWeek();
  }, [fetchWeek]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bizRes, svcRes, staffRes] = await Promise.all([
        fetch(`${API_URL}/bookings/public/business/${businessId}`),
        fetch(`${API_URL}/bookings/public/services/${businessId}`),
        fetch(`${API_URL}/bookings/public/staff/${businessId}`)
      ]);
      if (bizRes.ok) setBusiness(await bizRes.json());
      if (svcRes.ok) setServices(await svcRes.json());
      if (staffRes.ok) {
        const staffData = await staffRes.json();
        const staffList = Array.isArray(staffData) ? staffData : [];
        setStaff(staffList);
        // No staff configured — auto-select "Anyone Available" and skip staff step
        if (staffList.length === 0) setAnyoneAvailable(true);
      }
    } catch {
      setError('Could not load booking information.');
    } finally {
      setLoading(false);
    }
  };

  const selectStaffOption = (st: Staff | null, isAnyone: boolean) => {
    setSelectedStaff(st);
    setAnyoneAvailable(isAnyone);
    setSelectedSlot(null);
    setSelectedDate(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      const dateStr = selectedDate!.format('YYYY-MM-DD');
      const [slotHour, slotMin] = selectedSlot!.split(':').map(Number);
      const duration = selectedService?.duration || 60;
      const endMins = slotHour * 60 + slotMin + duration;
      const endHH = Math.floor(endMins / 60).toString().padStart(2, '0');
      const endMM = (endMins % 60).toString().padStart(2, '0');

      const payload: any = {
        businessId,
        customerName: values.name,
        customerEmail: values.email,
        customerPhone: values.phone,
        service: selectedService?.name,
        serviceId: selectedService?.id,
        date: dateStr,
        time: selectedSlot,
        startTime: selectedSlot,
        endTime: `${endHH}:${endMM}`,
        duration,
        notes: values.notes,
        price: selectedService?.price || 0,
        anyoneAvailable
      };
      if (!anyoneAvailable && selectedStaff) {
        payload.staffId = selectedStaff.id;
      }

      const response = await fetch(`${API_URL}/bookings/public/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const err = await response.json();
        setError(err.message || 'Failed to complete booking.');
      }
    } catch {
      setError('An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayAndBook = async (values: any) => {
    try {
      setSubmitting(true);
      const dateStr = selectedDate!.format('YYYY-MM-DD');
      const duration = selectedService?.duration || 60;
      const startISO = dayjs(`${dateStr}T${selectedSlot}`).toISOString();
      const endISO = dayjs(`${dateStr}T${selectedSlot}`).add(duration, 'minute').toISOString();

      const response = await fetch(`${API_URL}/bookings/public/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId,
          serviceName: selectedService?.name,
          price: selectedService?.price,
          customerEmail: values.email,
          bookingDetails: {
            customerName: values.name,
            customerPhone: values.phone,
            service: selectedService?.name,
            date: dateStr,
            time: selectedSlot,
            duration,
            startTime: startISO,
            endTime: endISO,
            staffId: anyoneAvailable ? undefined : selectedStaff?.id,
            anyoneAvailable,
            notes: values.notes,
            price: selectedService?.price
          }
        })
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const err = await response.json();
        setError(err.message || 'Payment initialization failed.');
      }
    } catch {
      setError('An error occurred during payment setup.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '100px' }}>
      <Spin size="large" tip="Loading Booking Portal..." />
    </div>
  );
  if (error && !business) return (
    <div style={{ maxWidth: 600, margin: '50px auto' }}>
      <Alert message="Error" description={error} type="error" showIcon />
    </div>
  );
  if (success) return (
    <div style={{ maxWidth: 700, margin: '50px auto', padding: '0 20px' }}>
      <Result
        status="success"
        title="Booking Confirmed!"
        subTitle={`Thank you for booking with ${business?.name}. We've sent a confirmation email to your inbox.`}
        extra={[
          <Button type="primary" key="close" onClick={() => window.close()}>
            Close Tab
          </Button>
        ]}
      />
    </div>
  );

  const hasNoStaff = staff.length === 0;

  const next = () => {
    setError(null);
    setCurrentStep(s => {
      // Skip staff step (step 1) when business has no staff
      if (s === 0 && hasNoStaff) return 2;
      return s + 1;
    });
  };

  const prev = () => {
    setError(null);
    setCurrentStep(s => {
      // Skip staff step (step 1) when business has no staff
      if (s === 2 && hasNoStaff) return 0;
      return s - 1;
    });
  };

  // Only show real staff members
  const displayStaff = staff;

  return (
    <div style={{ maxWidth: 820, margin: '40px auto', padding: '0 20px' }}>
      <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 12 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {business?.logo
            ? <Avatar src={business.logo} size={84} style={{ marginBottom: 16 }} />
            : <Avatar icon={<UserOutlined />} size={84} style={{ marginBottom: 16 }} />}
          <Title level={2} style={{ marginBottom: 4 }}>{business?.name}</Title>
          <Paragraph type="secondary">Book your appointment online in a few easy steps.</Paragraph>
        </div>

        <Steps current={currentStep} style={{ marginBottom: 40 }} size="small">
          <Steps.Step title="Service" icon={<InfoCircleOutlined />} />
          <Steps.Step title="Staff" icon={<TeamOutlined />} />
          <Steps.Step title="Date & Time" icon={<CalendarOutlined />} />
          <Steps.Step title="Your Details" icon={<UserOutlined />} />
        </Steps>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 24 }}
            onClose={() => setError(null)}
          />
        )}

        {/* ─── STEP 0: SELECT SERVICE ─── */}
        {currentStep === 0 && (
          <div>
            <Title level={4} style={{ marginBottom: 24 }}>What service are you looking for?</Title>
            {services.length === 0 && (
              <Alert type="warning" message="No services are currently available. Please check back later." showIcon />
            )}
            <Row gutter={[16, 16]}>
              {services.map(s => (
                <Col xs={24} sm={12} key={s.id}>
                  <Card
                    hoverable
                    onClick={() => setSelectedService(s)}
                    style={{
                      border: selectedService?.id === s.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                      borderRadius: 8
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Text strong>{s.name}</Text>
                      <Text strong type="success">£{s.price}</Text>
                    </div>
                    {s.duration && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <ClockCircleOutlined style={{ marginRight: 4 }} />{s.duration} min
                        </Text>
                      </div>
                    )}
                    <Paragraph ellipsis={{ rows: 2 }} style={{ marginTop: 8, color: '#8c8c8c', marginBottom: 0 }}>
                      {s.description}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
            <div style={{ marginTop: 40, textAlign: 'right' }}>
              <Button
                type="primary"
                size="large"
                disabled={!selectedService}
                onClick={next}
              >
                Choose Staff →
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 1: SELECT STAFF ─── */}
        {currentStep === 1 && (
          <div>
            <Title level={4} style={{ marginBottom: 8 }}>Who would you like to see?</Title>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
              Choose a specific staff member or let us pick someone available for you.
            </Paragraph>
            <Row gutter={[16, 16]}>
              {displayStaff.map(st => {
                const isSelected = selectedStaff?.id === st.id;
                return (
                  <Col xs={24} sm={12} md={8} key={st.id}>
                    <Card
                      hoverable
                      onClick={() => selectStaffOption(st, false)}
                      style={{
                        textAlign: 'center',
                        border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                        borderRadius: 8,
                        background: isSelected ? '#e6f7ff' : undefined
                      }}
                    >
                      <Avatar src={st.photo_url} icon={<UserOutlined />} size="large" />
                      <div style={{ marginTop: 8 }}>
                        <Text strong>{st.name}</Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {st.role === 'Admin' ? 'Owner' : st.role}
                      </Text>
                      {isSelected && (
                        <div style={{ marginTop: 6 }}>
                          <Badge status="processing" text={<Text type="secondary" style={{ fontSize: 11 }}>Selected</Text>} />
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
            {staff.length === 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 16 }}
                message="No individual staff members listed"
                description="You can proceed and we'll assign the next available team member."
              />
            )}
            <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-between' }}>
              <Button size="large" onClick={prev}>← Back</Button>
              <Button
                type="primary"
                size="large"
                disabled={!selectedStaff && !anyoneAvailable}
                onClick={next}
              >
                Pick a Time →
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 2: DATE & TIME CALENDAR ─── */}
        {currentStep === 2 && (
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>When would you like to come in?</Title>
            <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>
              {anyoneAvailable ? 'Any available staff' : selectedStaff ? `With ${selectedStaff.name}` : ''} &nbsp;·&nbsp; 30 min slots
            </p>

            {/* Week navigation */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <Button
                icon={<LeftOutlined />}
                onClick={() => { setWeekStart(w => w.subtract(1, 'week')); setSelectedSlot(null); setSelectedDate(null); }}
                disabled={weekStart.isBefore(dayjs().startOf('isoWeek'))}
                size="small"
              />
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                {weekStart.format('D MMM')} – {weekStart.add(6, 'day').format('D MMM YYYY')}
              </span>
              <Button
                icon={<RightOutlined />}
                onClick={() => { setWeekStart(w => w.add(1, 'week')); setSelectedSlot(null); setSelectedDate(null); }}
                size="small"
              />
            </div>

            {loadingWeek ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin tip="Loading availability…" />
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 480 }}>
                  <thead>
                    <tr>
                      {/* Time label column */}
                      <th style={{ width: 56, padding: '6px 4px', fontSize: 11, color: '#94a3b8', fontWeight: 600, borderBottom: '2px solid #e2e8f0', textAlign: 'center' }}>
                        TIME
                      </th>
                      {weekDays.map(day => {
                        const isToday = day.isSame(dayjs(), 'day');
                        const isPast  = day.isBefore(dayjs(), 'day');
                        return (
                          <th key={day.format('YYYY-MM-DD')} style={{
                            padding: '6px 4px',
                            fontSize: 12,
                            fontWeight: 700,
                            textAlign: 'center',
                            borderBottom: '2px solid #e2e8f0',
                            color: isPast ? '#cbd5e1' : isToday ? '#2563eb' : '#1e293b',
                            background: isToday ? '#eff6ff' : undefined,
                            borderRadius: isToday ? '8px 8px 0 0' : undefined,
                          }}>
                            <div>{day.format('ddd')}</div>
                            <div style={{ fontSize: 15, fontWeight: 800 }}>{day.format('D')}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_ROWS.map((time, rowIdx) => (
                      <tr key={time} style={{ background: rowIdx % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                        <td style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: '3px 2px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {time}
                        </td>
                        {weekDays.map(day => {
                          const dateStr = day.format('YYYY-MM-DD');
                          const isPast = day.isBefore(dayjs(), 'day') ||
                            (day.isSame(dayjs(), 'day') && time <= dayjs().format('HH:mm'));
                          const slotData = (weekSlots[dateStr] || []).find(s => s.time === time);
                          const available = !isPast && slotData?.available === true;
                          const isSelected = selectedDate?.format('YYYY-MM-DD') === dateStr && selectedSlot === time;

                          return (
                            <td key={dateStr} style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {available ? (
                                <button
                                  onClick={() => { setSelectedDate(day); setSelectedSlot(time); }}
                                  style={{
                                    width: '100%',
                                    padding: '5px 2px',
                                    borderRadius: 6,
                                    border: isSelected ? '2px solid #2563eb' : '1px solid #bbf7d0',
                                    background: isSelected
                                      ? 'linear-gradient(135deg,#2563eb,#60a5fa)'
                                      : '#dcfce7',
                                    color: isSelected ? '#fff' : '#166534',
                                    fontWeight: isSelected ? 700 : 500,
                                    fontSize: 11,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    boxShadow: isSelected ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                                  }}
                                >
                                  {isSelected ? '✓' : '✓'}
                                </button>
                              ) : (
                                <div style={{
                                  width: '100%',
                                  padding: '5px 2px',
                                  borderRadius: 6,
                                  background: isPast ? 'transparent' : '#f1f5f9',
                                  color: '#cbd5e1',
                                  fontSize: 10,
                                }}>
                                  {isPast ? '' : '–'}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Selected slot confirmation */}
            {selectedDate && selectedSlot && (
              <Alert
                style={{ marginTop: 16 }}
                type="success"
                showIcon
                message={
                  <span>
                    <strong>Selected:</strong> {selectedDate.format('dddd D MMMM YYYY')} at <strong>{selectedSlot}</strong>
                    {anyoneAvailable ? '' : selectedStaff ? ` with ${selectedStaff.name}` : ''}
                  </span>
                }
              />
            )}

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
              <Button size="large" onClick={prev}>← Back</Button>
              <Button
                type="primary"
                size="large"
                disabled={!selectedDate || !selectedSlot}
                onClick={next}
              >
                Enter My Details →
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 3: CUSTOMER DETAILS ─── */}
        {currentStep === 3 && (
          <Form form={form} layout="vertical" onFinish={() => {}}>
            <Title level={4} style={{ marginBottom: 24 }}>Almost there! Tell us about you.</Title>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="name" label="Full Name" rules={[{ required: true, message: 'Please enter your name' }]}>
                  <Input prefix={<UserOutlined />} size="large" placeholder="Jane Smith" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                  <Input prefix={<MailOutlined />} size="large" placeholder="name@example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="phone" label="Phone Number" rules={[{ required: true, message: 'Please enter your phone number' }]}>
                  <Input prefix={<PhoneOutlined />} size="large" placeholder="+44 7xxx xxxxxx" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label="Notes (Optional)">
                  <Input.TextArea rows={3} placeholder="Anything we should know?" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Booking summary */}
            <div style={{ marginBottom: 24, padding: 16, background: '#fafafa', borderRadius: 8 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Booking Summary</Text>
              <Space direction="vertical" size={4}>
                <div><Tag color="blue">{selectedService?.name}</Tag> <Text strong>£{selectedService?.price}</Text></div>
                <div>
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  {selectedDate?.format('dddd, MMMM D YYYY')} at {selectedSlot}
                </div>
                <div>
                  <UserOutlined style={{ marginRight: 6 }} />
                  {selectedStaff?.name || 'Auto-assigned'}
                </div>
                {selectedService?.duration && (
                  <div>
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    {selectedService.duration} minutes
                  </div>
                )}
              </Space>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Alert
                type="info"
                showIcon
                message="Stripe payments will be available soon"
                description="We are currently in Stripe settings mode; booking now will proceed without online payment. Once Stripe connection is fully enabled, Pay & Book will return."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <Button size="large" onClick={prev}>← Back</Button>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button
                  type="primary"
                  size="large"
                  loading={submitting}
                  onClick={() => form.validateFields().then(handleSubmit).catch(() => {})}
                >
                  ✅ Confirm & Book
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Card>
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Text type="secondary">Powered by ShahoSuite</Text>
      </div>
    </div>
  );
}
